import { MillenniumDate, MillenniumDateTime, SimpleDateFormat } from "@timeedit/millennium-time";
import Language from "../../lib/Language";

/*
 * Here are some temporary date and time utils for rendering activity time strings
 * in a tecore-client compiant format with the use of millennium-time.
 * We should try to extract these to shared utils (in schedule-fe-lib) for
 * rendering activity's time values in this way accross all of our products
 * that renders activity timing values.
 */

type TLanguageKey = Parameters<typeof Language.get>[0];

const MINUTES_PER_HOUR = 60;
const SECONDS_PER_MINUTE = 60;

const pad = (value: number) => String(value).padStart(2, "0");

const getDateInTimeZone = (date: Date, timeZone?: string) => {
    // A fixed locale keeps them in Western digits and the Gregorian calendar.
    // Only the numeric parts are read.
    const toParts = (zone?: string) =>
        new Intl.DateTimeFormat("en-US", {
            timeZone: zone,
            year: "numeric",
            month: "numeric",
            day: "numeric",
        }).formatToParts(date);
    let parts: Intl.DateTimeFormatPart[];
    try {
        // Try to parse given timezone string.
        parts = toParts(timeZone);
    } catch {
        parts = toParts("UTC");
    }
    const part = (type: Intl.DateTimeFormatPartTypes) =>
        Number(parts.find((entry) => entry.type === type)?.value);
    return MillenniumDate.create(part("year"), part("month"), part("day"));
};

export const formatActivityDate = (value: string | number, timeZone?: string): string => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return String(value);
    }
    return SimpleDateFormat.format(
        getDateInTimeZone(date, timeZone),
        Language.getDateFormat("date_f_yyyy_mm_dd")
    );
};

/** Minutes after midnight in the user's time format. */
const formatTimeOfDay = (value: unknown): string => {
    if (value === null || value === undefined || value === "") {
        return "";
    }
    const minutes = Number(value);
    if (!Number.isFinite(minutes) || minutes < 0) {
        return "";
    }
    return SimpleDateFormat.format(
        new MillenniumDateTime(minutes * SECONDS_PER_MINUTE),
        Language.getDateFormat("date_f_hh_mm")
    );
};

/** A length in minutes as "h:mm", (like a reservation's length). */
export const formatDuration = (minutes: number): string =>
    `${Math.floor(minutes / MINUTES_PER_HOUR)}:${pad(minutes % MINUTES_PER_HOUR)}`;

const weekdayKeys = {
    SUN: "zz_weekday_a_sunday",
    MON: "zz_weekday_b_monday",
    TUE: "zz_weekday_c_tuesday",
    WED: "zz_weekday_d_wednesday",
    THU: "zz_weekday_e_thursday",
    FRI: "zz_weekday_f_friday",
    SAT: "zz_weekday_g_saturday",
} as const satisfies Record<string, TLanguageKey>;

const formatWeekday = (value: unknown): string => {
    const key = weekdayKeys[String(value) as keyof typeof weekdayKeys];
    return key ? Language.get(key) : String(value);
};

const isPadding = (value: unknown): value is { after?: number | null } =>
    typeof value === "object" && value !== null && "after" in value;

export const getActivityTimingText = (timing: { extId: string; value: unknown }): string => {
    const values: unknown[] = Array.isArray(timing.value) ? timing.value : [timing.value];
    switch (timing.extId) {
        case "time":
            return values.map(formatTimeOfDay).filter(Boolean).join(", ");
        case "weekday":
            return values.map(formatWeekday).join(", ");
        case "padding":
            return values
                .filter(isPadding)
                .map((padding) => padding.after)
                .filter((after): after is number => typeof after === "number" && after >= 0)
                .map(formatDuration)
                .join(", ");
        default:
            return "";
    }
};
